import { Button } from "@/components/ui/button";
import { ConsolidatedFilterDropdown } from "@/components/ui/consolidated-filter-dropdown";
import {
  useAvailableSenderLabels,
  useBulkAddSenderLabels,
  useBulkRemoveSenderLabels,
} from "@/hooks/use-b2c-sender";
import { useGetActiveSubscriptionPlans } from "@/hooks/use-subscription";
import Trash from "@/components/icons/trash";

import { AgGridReact } from "ag-grid-react";
import {
  EllipsisVertical,
  Power,
  PowerOff,
  Users,
  MoreVertical,
  Pencil,
  Tag as TagIcon,
} from "lucide-react";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import DataTable from "@/components/data-table";
import { ColumnVisibility } from "@/components/data-table/column-visiblity";
import InitialPageVisitGuideModal from "@/components/page-visit-guide";
import { ConfirmActionPopup } from "@/components/popups/confirm-action";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SearchInput } from "@/components/ui/search-input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useBulkDeleteWarmupAccounts,
  useBulkWarmupAccountToggle,
  useDeleteWarmupAccount,
  useUpdateB2cSender,
  useWarmupAccounts,
} from "@/hooks/use-mailbox";
import { usePermission } from "@/hooks/use-premission";
import { dateFormatter, extractErrorMessage } from "@/lib/utils";
import { ISenderProfile } from "@/types/mailbox.types";
import { Link } from "@tanstack/react-router";
import { ColDef, ValueGetterParams } from "ag-grid-community";
import { toast } from "sonner";
import WarmupToggleCell from "./warmup-toggle-cell";
import { SenderLabelCell } from "./sender-label-cell";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import AssignSenderSubscriptionSelect from "@/pages/mailbox/assign-sender-subscription-select";
import ScoreBadge from "@/components/score-badge";
import { authProviderIcons } from "@/pages/mailbox/esp-providers";
import { cn } from "@/lib/utils";
import { espTypeDescription, AuthProvider } from "@/types/common";

const MAX_SENDERS = 50;

// Limit ESP options strictly to the ones defined in espTypeDescription
const espTypeOptions = espTypeDescription;

const senderProfileColDefs = ({
  handleDelete,
  handleEdit,
  hasPermission,
  subscriptionPlans,
}: {
  handleDelete: (sender: ISenderProfile) => void;
  handleEdit: (sender: ISenderProfile) => void;
  hasPermission: (permission: any) => boolean;
  subscriptionPlans?: any[];
}): ColDef<ISenderProfile>[] => [
  {
    headerName: "Warmup",
    field: "isWarmupEnabled",
    maxWidth: 80,
    minWidth: 80,
    filter: false,
    valueGetter: (params: ValueGetterParams<ISenderProfile>) =>
      params.data?.isWarmupEnabled ? "Yes" : "No",
    cellRenderer: (params: { data: ISenderProfile }) => (
      <WarmupToggleCell data={params.data} type="sender_profile" />
    ),
  },
  {
    field: "email",
    headerName: "Sender Profile",
    minWidth: 200,
    cellRenderer: (params: { data: ISenderProfile }) => (
      <Link
        to="/mailboxes/$mailboxid"
        params={{ mailboxid: params.data._id }}
        search={{ sender: params.data.email }}
      >
        {params.data.email}
      </Link>
    ),
  },
  {
    field: "firstName",
    headerName: "First Name",
    minWidth: 120,
    cellRenderer: (params: { data: any }) => {
      return <span>{params.data.firstName || "-"}</span>;
    },
  },
  {
    field: "lastName",
    headerName: "Last Name",
    minWidth: 120,
    cellRenderer: (params: { data: any }) => {
      return <span>{params.data.lastName || "-"}</span>;
    },
  },
  {
    field: "labels",
    headerName: "Labels",
    minWidth: 160,
    filter: false,
    cellRenderer: (params: { data: ISenderProfile }) => (
      <SenderLabelCell data={params.data} />
    ),
  },
  {
    field: "authProvider",
    headerName: "Sender ESP Type",
    minWidth: 120,
    valueGetter: (params: ValueGetterParams<ISenderProfile>) => {
      // Prefer new espType field, fallback to legacy authProvider
      const espType = (params.data as any)?.espType || (params.data as any)?.authProvider;
      return espType || "-";
    },
    cellRenderer: (params: { data: ISenderProfile }) => {
      // Use espType from new API, fallback to authProvider
      const espType = params.data.espType || params.data.authProvider;
      if (!espType) return null;
      
      // Get icon mapping - espType might be a string from API
      const espTypeStr = String(espType);
      // Try multiple variations: exact match, lowercase, with spaces replaced by underscores, etc.
      const normalized = espTypeStr.replace(/\s+/g, '_').toLowerCase();
      const iconMapping = 
        authProviderIcons[espTypeStr] || 
        authProviderIcons[espTypeStr.toLowerCase()] ||
        authProviderIcons[espTypeStr.replace(/\s+/g, '_')] ||
        authProviderIcons[normalized] ||
        authProviderIcons[espTypeStr.replace(/\s+/g, '')];
      
      if (!iconMapping) {
        // Return default icon with espType text if no mapping found
        return (
          <div className="flex items-center h-full">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-xs">{espType}</span>
              </TooltipTrigger>
              <TooltipContent>
                ESP Type: {espType}
              </TooltipContent>
            </Tooltip>
          </div>
        );
      }
      
      return (
        <div className="flex items-center h-full">
          <Tooltip>
            <TooltipTrigger asChild>
              {iconMapping.icon}
            </TooltipTrigger>
            <TooltipContent>
              {iconMapping.tooltip}
            </TooltipContent>
          </Tooltip>
        </div>
      );
    },
  },
  // {
  //   field: "seedAssigned",
  //   headerName: "Seed Assigned",
  //   minWidth: 150,
  //   cellRenderer: (params: { data: any }) => {
  //     if (!params.data.seedAssigned || params.data.seedAssigned === "-") {
  //       return <span>-</span>;
  //     }
      
  //     // Handle seedAssigned - it might be a string with ESP type names
  //     // Could be a single value or comma-separated values
  //     let espTypes: string[] = [];
      
  //     if (typeof params.data.seedAssigned === 'string') {
  //       // Split by comma if it contains multiple values, otherwise use as single value
  //       espTypes = params.data.seedAssigned.includes(',') 
  //         ? params.data.seedAssigned.split(',').map((s: string) => s.trim())
  //         : [params.data.seedAssigned.trim()];
  //     } else if (Array.isArray(params.data.seedAssigned)) {
  //       espTypes = params.data.seedAssigned;
  //     }
      
  //     if (espTypes.length === 0) {
  //       return <span>-</span>;
  //     }
      
  //     return (
  //       <div className="flex items-center gap-2 h-full flex-wrap">
  //         {espTypes.map((espType: string, index: number) => {
  //           // Normalize the ESP type name (lowercase, trim, replace spaces/dashes with underscores)
  //           const espTypeStr = String(espType).trim().toLowerCase();
  //           const normalizedType = espTypeStr.replace(/[\s-]+/g, "_");
            
  //           // Try multiple variations: normalized (with underscores), original lowercase, and with dashes
  //           const iconMapping = authProviderIcons[normalizedType] 
  //             || authProviderIcons[espTypeStr]
  //             || authProviderIcons[espTypeStr.replace(/_/g, "-")]
  //             || authProviderIcons[String(espType).toLowerCase()];
            
  //           if (!iconMapping) {
  //             // Fallback to text if no icon found
  //             return (
  //               <Tooltip key={index}>
  //                 <TooltipTrigger asChild>
  //                   <span className="text-xs">{espType}</span>
  //                 </TooltipTrigger>
  //                 <TooltipContent>
  //                   {espType}
  //                 </TooltipContent>
  //               </Tooltip>
  //             );
  //           }
            
  //           return (
  //             <Tooltip key={index}>
  //               <TooltipTrigger asChild>
  //                 {iconMapping.icon}
  //               </TooltipTrigger>
  //               <TooltipContent>
  //                 {iconMapping.tooltip}
  //               </TooltipContent>
  //             </Tooltip>
  //           );
  //         })}
  //       </div>
  //     );
  //   },
  // },
  {
    field: "seedEspTypes",
    headerName: "Seed ESP Types",
    minWidth: 150,
    cellRenderer: (params: { data: any }) => {
      if (!params.data.seedEspTypes || !Array.isArray(params.data.seedEspTypes) || params.data.seedEspTypes.length === 0) {
        return <span>-</span>;
      }
      
      return (
        <div className="flex items-center gap-2 h-full flex-wrap">
          {params.data.seedEspTypes.map((espType: string, index: number) => {
            // Normalize the ESP type name (lowercase, trim, replace spaces/dashes with underscores)
            const espTypeStr = String(espType).trim().toLowerCase();
            const normalizedType = espTypeStr.replace(/[\s-]+/g, "_");
            // Try multiple variations: normalized (with underscores), original lowercase, and with dashes
            const iconMapping = authProviderIcons[normalizedType] 
              || authProviderIcons[espTypeStr]
              || authProviderIcons[espTypeStr.replace(/_/g, "-")]
              || authProviderIcons[String(espType).toLowerCase()];
            
            if (!iconMapping) {
              // Fallback to text if no icon found
              return (
                <Tooltip key={index}>
                  <TooltipTrigger asChild>
                    <span className="text-xs">{espType}</span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {espType}
                  </TooltipContent>
                </Tooltip>
              );
            }
            
            return (
              <Tooltip key={index}>
                <TooltipTrigger asChild>
                  {iconMapping.icon}
                </TooltipTrigger>
                <TooltipContent>
                  {iconMapping.tooltip}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      );
    },
  },
  {
    field: "gmailSeeds",
    headerName: "Gmail Seeds",
    minWidth: 120,
    cellRenderer: (params: { data: any }) => {
      return <span>{params.data.gmailSeeds || 0}</span>;
    },
  },
  {
    field: "outlookSeeds",
    headerName: "Outlook Seeds",
    minWidth: 130,
    cellRenderer: (params: { data: any }) => {
      return <span>{params.data.outlookSeeds || 0}</span>;
    },
  },
  {
    field: "yahooSeeds",
    headerName: "Yahoo Seeds",
    minWidth: 120,
    cellRenderer: (params: { data: any }) => {
      return <span>{params.data.yahooSeeds || 0}</span>;
    },
  },
  {
    field: "googleWorkspaceSeeds",
    headerName: "Google Workspace Seeds",
    minWidth: 180,
    cellRenderer: (params: { data: any }) => {
      return <span>{params.data.googleWorkspaceSeeds || 0}</span>;
    },
  },
  {
    field: "microsoft365Seeds",
    headerName: "Microsoft 365 Seeds",
    minWidth: 210,
    cellRenderer: (params: { data: any }) => {
      return <span>{params.data.microsoft365Seeds || 0}</span>;
    },
  },
  {
    headerName: "Assigned Subscription",
    colId: "assignedSubscription",
    minWidth: 200,
    valueGetter: (params: any) => {
      if (!subscriptionPlans || !params.data) return "";
      
      const { subscriptionId, planId, addonPlan } = params.data;
      
      if (!subscriptionId) return "No subscription assigned";
      
      // Find the subscription
      const subscription = subscriptionPlans.find((sub) => sub._id === subscriptionId);
      if (!subscription) return "Subscription not found";
      
      // Find the plan within the subscription
      const plan = subscription.plans.find((p: any) => {
        // Check for custom plan (addonPlan)
        if (addonPlan && p?.isCustomPlan && p?.addonPlanId?._id === addonPlan) {
          return true;
        }
        // Check for regular plan
        if (planId && p?.planId?._id === planId) {
          return true;
        }
        return false;
      });
      
      if (!plan) return "Plan not found";
      
      // Format the label similar to assign-sender-subscription-select
      if (plan?.isCustomPlan && plan?.addonPlanId?._id) {
        return `Custom Plan (${plan.addonPlanId.maxEmailsPerDay} emails/day) (${subscription.interval})`;
      }
      
      if (plan?.planId?._id) {
        const isSeedListPlan = plan.planId.isOnDemandSenderProfile || false;
        const espTypes = plan.planId.espTypes || plan.planId.senderProfileMetadata?.espTypes || [];
        const seedCountPerSender = plan.planId.seedCountPerSender || plan.planId.senderProfileMetadata?.baseSeedCount || 0;
        
        // Generate label for seed list plans
        if (isSeedListPlan && espTypes.length > 0) {
          // Format ESP type names
          const formatEspTypeName = (espType: string): string => {
            const espTypeStr = String(espType).trim();
            const displayNames: Record<string, string> = {
              "Gmail": "Gmail",
              "gmail": "Gmail",
              "Outlook": "Outlook",
              "outlook": "Outlook",
              "Yahoo": "Yahoo",
              "yahoo": "Yahoo",
              "Google Workspace": "Google Workspace",
              "Google-Workspace": "Google Workspace",
              "google_workspace": "Google Workspace",
              "Microsoft 365": "Microsoft 365",
              "Microsoft365": "Microsoft 365",
              "microsoft_365": "Microsoft 365",
              "AOL": "AOL",
              "iCloud": "iCloud",
            };
            return displayNames[espTypeStr] || espTypeStr;
          };
          
          // For single ESP type
          if (espTypes.length === 1) {
            const espTypeName = formatEspTypeName(espTypes[0]);
            return `${espTypeName} Seeds (${seedCountPerSender.toLocaleString()}) (${subscription.interval})`;
          }
          
          // For multiple ESP types
          const formattedEspTypes = espTypes.map(formatEspTypeName).join(", ");
          return `${formattedEspTypes} Seeds (${seedCountPerSender.toLocaleString()}) (${subscription.interval})`;
        }
        
        // For regular plans
        const category = plan.planId.category || "";
        const planType = plan.planId.planType || "";
        return `${category} - ${planType} (${subscription.interval})`;
      }
      
      return "Unknown plan";
    },
    cellRenderer: (params: { data: ISenderProfile }) => (
      <div className="flex items-center h-full">
        <AssignSenderSubscriptionSelect
          senderId={params.data._id}
          senderEmail={params.data.email}
          subscriptionId={params.data.subscriptionId || null}
          planId={params.data.planId || null}
          addonPlanId={params.data.addonPlan || null}
          pageType="list"
          targetAudience={params.data.targetAudience}
        />
      </div>
    ),
  },
  {
    field: "createdAt",
    headerName: "Created At",
    minWidth: 190,
    cellRenderer: (params: { data: ISenderProfile }) => {
      return <span>{dateFormatter(params.data.createdAt)}</span>;
    },
  },
  {
    colId: "statusLabel",
    headerName: "Sender Status",
    minWidth: 100,
    pinned: "right",
    cellRenderer: (params: { data: ISenderProfile }) => {
      const statusLabel = ((params.data as any)?.statusLabel as string)?.toLowerCase() || "";
      
      // Map status labels to badge configurations
      const getStatusConfig = () => {
        if (statusLabel.includes("warming up") || statusLabel.includes("warming")) {
          return {
            label: "Warming Up",
            tooltip: "Building reputation. Do not start campaigns yet.",
            className: "bg-yellow-100 hover:bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400",
            dotColor: "bg-yellow-500",
          };
        }
        if (statusLabel.includes("ready")) {
          return {
            label: "Ready",
            tooltip: "Safe to send. Mailbox is healthy and fully primed.",
            className: "bg-green-100 hover:bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
            dotColor: "bg-green-500",
          };
        }
        if (statusLabel.includes("cool down") || statusLabel.includes("cooldown")) {
          return {
            label: "Cool Down",
            tooltip: "This Sender Profile entered Cool Down as the last 5 days' warmup spam rate crossed 5%. Pause email campaigns temporarily to protect your domain reputation.",
            className: "bg-red-100 hover:bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
            dotColor: "bg-red-500",
          };
        }
        if (statusLabel.includes("paused")) {
          return {
            label: "Paused",
            tooltip: "Turn on warmups to build reputation",
            className: "bg-blue-100 hover:bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400",
            dotColor: "bg-blue-500",
          };
        }
        // Default fallback
        const originalStatus = (params.data as any)?.statusLabel || "Unknown";
        return {
          label: originalStatus,
          tooltip: originalStatus !== "Unknown" ? originalStatus : "Unknown status",
          className: "bg-gray-100 hover:bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400",
          dotColor: "bg-gray-500",
        };
      };

      const config = getStatusConfig();

      return (
        <div className="h-full flex items-center justify-start">
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant="default"
                className={cn(
                  "h-fit rounded-md space-x-1.5 shadow-none flex items-center gap-1.5",
                  config.className
                )}
              >
                <span className={cn("w-2 h-2 rounded-full", config.dotColor)} />
                <span>{config.label}</span>
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <span>{config.tooltip}</span>
            </TooltipContent>
          </Tooltip>
        </div>
      );
    },
  },
  // {
  //   colId: "openPercentages-deliveryRate",
  //   headerName: "Deliverability Score",
  //   sortable: true,
  //   pinned: "right",
  //   filter: false,
  //   width: 70,
  //   minWidth: 70,
  //   maxWidth: 70,
  //   headerComponent: () => {
  //     return (
  //       <Tooltip>
  //         <TooltipTrigger asChild>
  //           <div className="cursor-pointer flex items-center gap-2 h-full w-20">
  //             <span className="truncate flex-1">Deliverability Score</span>
  //           </div>
  //         </TooltipTrigger>
  //         <TooltipContent>
  //           <span>Deliverability Score</span>
  //         </TooltipContent>
  //       </Tooltip>
  //     );
  //   },
  //   cellRenderer: (params: any) => {
  //     const gmailScore = params.data.inboxGmail || 0;
  //     const outlookScore = params.data.inboxOutlook || 0;
  //     const yahooScore = params.data.inboxYahoo|| 0;
  //     const combinedScore = Math.round((gmailScore + outlookScore + yahooScore) / 3);
  //     return (
  //       <div className="flex items-center justify-center h-full">
  //         <ScoreBadge
  //           score={combinedScore}
  //           thresholds={{ green: 90, yellow: 70 }}
  //         />
  //       </div>
  //     );
  //   },
  // },
  {
    field: "inboxGmail",
    colId: "openPercentages-gmail",
    headerName: "Gmail",
    sortable: true,
    // pinned: "right",
    filter: false,
    width: 60,
    minWidth: 60,
    maxWidth: 60,
    valueGetter: (params: any) =>
      params.data?.openPercentages?.inboxGmail ?? params.data?.inboxGmail ?? 0,
    headerComponent: () => {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="cursor-pointer flex items-center gap-2 h-full">
              <span className="truncate flex-1">Gmail</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <span>Personal Gmail Reputation Score</span>
          </TooltipContent>
        </Tooltip>
      );
    },
    cellRenderer: (params: any) => {
      const score = params.data.inboxGmail || 0;
      return (
        <div className="flex items-center justify-center h-full">
          <ScoreBadge
            score={score}
            thresholds={{ green: 90, yellow: 70 }}
          />
        </div>
      );
    },
  },
  {
    field: "inboxOutlook",
    colId: "openPercentages-outlook",
    headerName: "Outlook",
    sortable: true,
    // pinned: "right",
    filter: false,
    width: 100,
    minWidth: 100,
    maxWidth: 100,
    valueGetter: (params: any) =>
      params.data?.openPercentages?.inboxOutlook ?? params.data?.inboxOutlook ?? 0,
    headerComponent: () => {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="cursor-pointer flex items-center gap-2 h-full">
              <span className="truncate flex-1">Outlook</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <span>Personal Outlook Reputation Score</span>
          </TooltipContent>
        </Tooltip>
      );
    },
    cellRenderer: (params: any) => {
      const score = params.data.inboxOutlook|| 0;
      return (
        <div className="flex items-center justify-center h-full">
          <ScoreBadge
            score={score}
            thresholds={{ green: 90, yellow: 70 }}
          />
        </div>
      );
    },
  },
  {
    field: "inboxYahoo",
    colId: "openPercentages-yahoo",
    headerName: "Yahoo",
    sortable: true,
    // pinned: "right",
    filter: false,
    width: 60,
    minWidth: 60,
    maxWidth: 60,
    valueGetter: (params: any) =>
      params.data?.openPercentages?.inboxYahoo ?? params.data?.inboxYahoo ?? 0,
    headerComponent: () => {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="cursor-pointer flex items-center gap-2 h-full">
              <span className="truncate flex-1">Yahoo</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <span>Personal Yahoo reputation score</span>
          </TooltipContent>
        </Tooltip>
      );
    },
    cellRenderer: (params: any) => {
      const score = params.data.inboxYahoo || 0;
      return (
        <div className="flex items-center justify-center h-full">
          <ScoreBadge
            score={score}
            thresholds={{ green: 90, yellow: 70 }}
          />
        </div>
      );
    },
  },
  {
    field: "inboxZoho",
    colId: "openPercentages-zoho",
    headerName: "Zoho",
    sortable: true,
    // pinned: "right",
    filter: false,
    width: 60,
    minWidth: 60,
    maxWidth: 60,
    valueGetter: (params: any) =>
      params.data?.openPercentages?.inboxZoho ?? params.data?.inboxZoho ?? 0,
    headerComponent: () => {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="cursor-pointer flex items-center gap-2 h-full">
              <span className="truncate flex-1">Zoho</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <span>Personal Zoho reputation score</span>
          </TooltipContent>
        </Tooltip>
      );
    },
    cellRenderer: (params: any) => {
      const score = params.data.inboxYahoo || 0;
      return (
        <div className="flex items-center justify-center h-full">
          <ScoreBadge
            score={score}
            thresholds={{ green: 90, yellow: 70 }}
          />
        </div>
      );
    },
  },
  {
    field: "inboxGoogleWorkspace",
    colId: "inboxGoogleWorkspace",
    headerName: "Google Workspace",
    sortable: true,
    // pinned: "right",
    filter: false,
    width: 90,
    minWidth: 90,
    maxWidth: 90,
    headerComponent: () => {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="cursor-pointer flex items-center gap-2 h-full">
              <span className="truncate flex-1">Google Workspace</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <span>Google Reputation Score</span>
          </TooltipContent>
        </Tooltip>
      );
    },
    cellRenderer: (params: any) => {
      const score = params.data?.inboxGoogleWorkspace|| 0;
      return (
        <div className="flex items-center justify-center h-full">
          <ScoreBadge
            score={score}
            thresholds={{ green: 90, yellow: 70 }}
          />
        </div>
      );
    },
  },
  {
    field: "inboxMicrosoft365",
    colId: "inboxMicrosoft365",
    headerName: "Microsoft 365",
    sortable: true,
    // pinned: "right",
    filter: false,
    width: 100,
    minWidth: 100,
    maxWidth: 100,
    headerComponent: () => {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="cursor-pointer flex items-center gap-2 h-full">
              <span className="truncate flex-1">Microsoft 365</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <span>Microsoft Reputation Score</span>
          </TooltipContent>
        </Tooltip>
      );
    },
    cellRenderer: (params: any) => {
      const score = params.data.inboxMicrosoft365 || 0;
      return (
        <div className="flex items-center justify-center h-full">
          <ScoreBadge
            score={score}
            thresholds={{ green: 90, yellow: 70 }}
          />
        </div>
      );
    },
  },
  {
    colId: "actions",
    headerName: "Actions",
    sortable: false,
    filter: false,
    maxWidth: 90,
    width: 90,
    pinned: "right",
    resizable: false,
    suppressMovable: true,
    cellRenderer: (params: { data: ISenderProfile }) => {
      const hasDeletePermission = hasPermission("mailboxes:delete");
      const hasEditPermission = hasPermission("mailboxes:edit");

      if (!hasDeletePermission && !hasEditPermission) return null;
      return (
        <div className="flex items-center justify-end h-full pr-4">
          <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {hasEditPermission && (
              <DropdownMenuItem
                onSelect={() => handleEdit(params.data)}
                className="group"
              >
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
            )}
            {hasDeletePermission && (
              <DropdownMenuItem
                onSelect={() => handleDelete(params.data)}
                className="delete-button group"
              >
                <Trash className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
      );
    },
  },
];

const SenderPageContent = () => {
  const gridRef = useRef<AgGridReact>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [selectedRows, setSelectedRows] = useState<ISenderProfile[]>([]);

  // Filter states
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [selectedSeedAssigned, setSelectedSeedAssigned] = useState<string[]>([]);
  const [selectedSendingVolume, setSelectedSendingVolume] = useState<string[]>([]);
  const [selectedSubscription, setSelectedSubscription] = useState<string[]>([]);

  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [showSingleDeleteDialog, setShowSingleDeleteDialog] = useState(false);
  const [toDeleteSender, setToDeleteSender] = useState<ISenderProfile | null>(
    null
  );
  
  // Edit dialog state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [toEditSender, setToEditSender] = useState<ISenderProfile | null>(null);
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editEspType, setEditEspType] = useState<string>("");

  const [showBulkLabelsDialog, setShowBulkLabelsDialog] = useState(false);
  const [bulkLabelsAction, setBulkLabelsAction] = useState<"add" | "remove">("add");
  const [bulkLabelsSelected, setBulkLabelsSelected] = useState<string[]>([]);

  const { hasPermission } = usePermission();

  // Sender profile labels: GET /b2c-sender/available-labels (SENDER_PROFILE only)
  const { data: availableSenderLabels = [] } = useAvailableSenderLabels();
  const { data: subscriptionPlans } = useGetActiveSubscriptionPlans();

  const labelOptionsForFilter = useMemo(
    () =>
      availableSenderLabels.map((label) => ({
        value: label._id,
        label: label.name,
        bgcolor: label.color || "",
      })),
    [availableSenderLabels]
  );

  // API Hooks
  const {
    mutateAsync: bulkWarmupAccountToggle,
    isPending: isBulkWarmupAccountToggling,
  } = useBulkWarmupAccountToggle();

  const { data: warmupAccountsResponse, isLoading: isWarmupLoading } =
    useWarmupAccounts({
      labels: selectedLabels,
      planIds: selectedSubscription,
      page: currentPage,
      limit: limit,
    });
  const { mutateAsync: deleteWarmup } = useDeleteWarmupAccount();
  const { mutateAsync: bulkDelete, isPending: isBulkDeletingWarmups } =
    useBulkDeleteWarmupAccounts();
  const { mutateAsync: updateSender, isPending: isUpdatingSender } =
    useUpdateB2cSender();
  const { mutateAsync: bulkAddSenderLabels, isPending: isBulkAddingLabels } =
    useBulkAddSenderLabels();
  const { mutateAsync: bulkRemoveSenderLabels, isPending: isBulkRemovingLabels } =
    useBulkRemoveSenderLabels();

  // Extract data array from response
  const warmupAccounts = warmupAccountsResponse?.data || [];

  // Create subscription options - only seed list plans for sender profiles
  const subscriptionOptions = useMemo(() => {
    if (!subscriptionPlans) return [];
    
    return subscriptionPlans
      .flatMap((subscription) =>
        subscription.plans.map((plan) => {
          // Only include seed list plans (isOnDemandSenderProfile)
          if (plan?.planId?._id) {
            const isSeedListPlan = plan.planId.isOnDemandSenderProfile || false;
            if (!isSeedListPlan) {
              return null; // Skip non-seed list plans
            }
            
            return {
              key: `${subscription._id}-${plan.planId._id}`,
              label: `${plan.planId.category} - ${plan.planId.planType}`,
              value: plan.planId._id,
              bgcolor: "#6b7280",
              category: plan.planId.category,
            };
          }
          return null;
        })
      )
      .filter((option): option is NonNullable<typeof option> => option !== null);
  }, [subscriptionPlans]);

  // Allow export trigger from header actions via a window event
  useEffect(() => {
    const handleExport = () => {
      const displayed = gridRef.current?.api.getAllDisplayedColumns?.() || [];
      const columnKeys = displayed
        .map((c: any) => c.getColId?.() ?? c.colId)
        .filter((id: string) => id !== "actions");
      gridRef.current?.api.exportDataAsCsv({
        fileName: "sender-profiles.csv",
        columnKeys,
      });
    };
    window.addEventListener("sender-profiles-export-csv", handleExport as EventListener);
    return () => {
      window.removeEventListener("sender-profiles-export-csv", handleExport as EventListener);
    };
  }, []);

  // Seed assigned options - dynamically generated from data
  const seedAssignedOptions = useMemo(() => {
    if (!warmupAccounts) return [];
    
    const uniqueSeeds = new Set<string>();
    warmupAccounts.forEach((account: any) => {
      if (account.seedAssigned && account.seedAssigned !== "-") {
        uniqueSeeds.add(account.seedAssigned);
      }
    });
    
    return Array.from(uniqueSeeds).sort((a, b) => {
      const numA = parseInt(a) || 0;
      const numB = parseInt(b) || 0;
      return numA - numB;
    }).map(seed => ({
      value: seed,
      label: seed,
      bgcolor: "#6b7280",
    }));
  }, [warmupAccounts]);
  
  // Sending volume options - dynamically generated from data
  const sendingVolumeOptions = useMemo(() => {
    if (!warmupAccounts) return [];
    
    const uniqueVolumes = new Set<string>();
    warmupAccounts.forEach((account: any) => {
      if (account.seedSendingVolume && account.seedSendingVolume !== "-") {
        uniqueVolumes.add(account.seedSendingVolume);
      }
    });
    
    return Array.from(uniqueVolumes).sort((a, b) => {
      const numA = parseInt(a) || 0;
      const numB = parseInt(b) || 0;
      return numA - numB;
    }).map(volume => ({
      value: volume,
      label: volume,
      bgcolor: "#6b7280",
    }));
  }, [warmupAccounts]);

  const onSelectionChanged = () => {
    const selectedNodes = gridRef?.current?.api.getSelectedNodes();
    const selectedData = selectedNodes?.map((node) => node.data) || [];
    setSelectedRows(selectedData);
  };

  // ------------------- Bulk Actions Sender Profiles -------------------

  const handleBulkWarmupSenderProfiles = async (enable: boolean) => {
    try {
      // TODO: update this after sender profile pricing is implemented
      toast.error(
        "Sender profiles must have subscription assigned to toggle warmup"
      );
      return;

      await bulkWarmupAccountToggle({
        ids: selectedRows.map((row) => row._id),
        enabled: enable,
      });
      toast.success(
        `Warmup ${enable ? "enabled" : "disabled"} for selected senders`
      );

      const eventName = enable
        ? "sender_profiles_bulk_warmup_toggled_on"
        : "sender_profiles_bulk_warmup_toggled_off";
      window.cioanalytics?.track(eventName, {
        count: selectedRows.length,
        emails: selectedRows.map((row) => row.email),
      });
    } catch (error) {
      const errorMessage = extractErrorMessage(
        error,
        "Failed to update warmup status"
      );
      toast.error(errorMessage);
    }
  };

  // handle single sender delete
  const confirmSingleDelete = async () => {
    if (!toDeleteSender) return;

    try {
      await deleteWarmup(toDeleteSender._id);
      setShowSingleDeleteDialog(false);
      setToDeleteSender(null);
      toast.success("Warmup account deleted successfully");

      window.cioanalytics?.track("sender_profile_deleted", {
        email: toDeleteSender.email,
      });
    } catch (error) {
      const errorMessage = extractErrorMessage(
        error,
        "Failed to delete warmup account"
      );
      toast.error(errorMessage);
    }
  };

  // handler for bulk deletion of senders
  const handleBulkDeleteSenders = async () => {
    try {
      await bulkDelete(selectedRows.map((row) => row._id));
      setShowBulkDeleteDialog(false);
      setSelectedRows([]);

      window.cioanalytics?.track("sender_profiles_bulk_deleted", {
        count: selectedRows.length,
        emails: selectedRows.map((row) => row.email),
      });

      toast.success(`Successfully deleted ${selectedRows.length} senders`);
    } catch (error) {
      const errorMessage = extractErrorMessage(
        error,
        "Failed to delete senders"
      );
      toast.error(errorMessage);
    }
  };

  const handleDelete = useCallback((sender: ISenderProfile) => {
    setToDeleteSender(sender);
    setShowSingleDeleteDialog(true);
  }, []);

  const handleEdit = useCallback((sender: ISenderProfile) => {
    setToEditSender(sender);
    setEditFirstName(sender.firstName || "");
    setEditLastName(sender.lastName || "");
    // Get espType, fallback to authProvider for backward compatibility
    const currentEspType = sender.espType || sender.authProvider || "";
    setEditEspType(String(currentEspType) || "");
    setShowEditDialog(true);
  }, []);

  const handleUpdateSender = async () => {
    if (!toEditSender) return;

    try {
      await updateSender({
        id: toEditSender._id,
        updates: {
          firstName: editFirstName,
          lastName: editLastName,
          espType: editEspType || undefined,
        },
      });
      
      setShowEditDialog(false);
      setToEditSender(null);
      setEditFirstName("");
      setEditLastName("");
      setEditEspType("");
      toast.success("Sender profile updated successfully");
      
      window.cioanalytics?.track("sender_profile_updated", {
        email: toEditSender.email,
        firstName: editFirstName,
        lastName: editLastName,
        espType: editEspType,
      });
    } catch (error) {
      const errorMessage = extractErrorMessage(
        error,
        "Failed to update sender profile"
      );
      toast.error(errorMessage);
    }
  };

  // Filter handlers
  const handleLabelsChange = (newLabels: string[]) => {
    setSelectedLabels(newLabels);
  };

  const handleSubscriptionChange = (newSubscription: string[]) => {
    setSelectedSubscription(newSubscription);
  };

  const handleSeedAssignedChange = (newSeedAssigned: string[]) => {
    setSelectedSeedAssigned(newSeedAssigned);
  };

  const handleSendingVolumeChange = (newSendingVolume: string[]) => {
    setSelectedSendingVolume(newSendingVolume);
  };

  // Filter the data based on selected filters
  // Note: Server-side filtering is now handled by the API
  // Client-side filtering for seed volume is still needed
  const filteredAccounts = useMemo(() => {
    if (!warmupAccounts || !Array.isArray(warmupAccounts)) return [];
    
    return warmupAccounts.filter((account: any) => {
      // Client-side filtering for seed assigned (if not supported by API yet)
      if (selectedSeedAssigned.length > 0) {
        if (!account.seedAssigned || account.seedAssigned === "-") return false;
        if (!selectedSeedAssigned.includes(account.seedAssigned)) return false;
      }
      
      // Client-side filtering for sending volume (if not supported by API yet)
      if (selectedSendingVolume.length > 0) {
        if (!account.seedSendingVolume || account.seedSendingVolume === "-") return false;
        if (!selectedSendingVolume.includes(account.seedSendingVolume)) return false;
      }
      
      return true;
    });
  }, [warmupAccounts, selectedSeedAssigned, selectedSendingVolume]);

  const columnDefs = useMemo(() => {
    const defs = senderProfileColDefs({
      handleDelete: handleDelete,
      handleEdit: handleEdit,
      hasPermission: hasPermission,
      subscriptionPlans,
    });

    if (!hasPermission("mailboxes:delete") && !hasPermission("mailboxes:edit")) {
      return defs.filter((def) => def.colId !== "actions");
    }
    return defs;
  }, [handleDelete, handleEdit, hasPermission, subscriptionPlans]);

  // handle search filter text
  const onFilterTextBoxChanged = useCallback(
    (searchTerm: string) => {
      if (gridRef.current?.api) {
        gridRef.current.api.setGridOption("quickFilterText", searchTerm);
      }
    },
    [gridRef]
  );

  const statCards = useMemo(() => {
    if (!warmupAccounts || !Array.isArray(warmupAccounts)) return [];

    const countSummary = warmupAccounts.reduce(
      (acc, account) => {
        if (account.isWarmupEnabled) {
          acc.warmupOn++;
        } else {
          acc.warmupOff++;
        }
        
        // Check if sender profile is connected (has subscription assigned)
        if (account.subscriptionId && (account.planId || account.addonPlan)) {
          acc.connected++;
        } else {
          acc.disconnected++;
        }
        
        return acc;
      },
      { warmupOn: 0, warmupOff: 0, connected: 0, disconnected: 0 }
    );

    return [
      {
        id: "sender-total",
        icon: Users,
        title: "Total Sender Profiles",
        value: warmupAccounts.length || 0,
      },
      {
        id: "sender-warmup-on",
        icon: Power,
        title: "Total Warmup On",
        value: countSummary.warmupOn || 0,
      },
      {
        id: "sender-warmup-off",
        icon: PowerOff,
        title: "Total Warmup Off",
        value: countSummary.warmupOff || 0,
      },
    ];
  }, [warmupAccounts]);

  const combinedLoading =
    isBulkDeletingWarmups ||
    isBulkWarmupAccountToggling ||
    isBulkAddingLabels ||
    isBulkRemovingLabels;

  return (
    <>
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
          {isWarmupLoading || !warmupAccounts
            ? Array.from({ length: 3 }).map((_, i) => (
                <Card key={i} className="w-full">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Skeleton className="size-8 shrink-0" />
                      <Skeleton className="h-5 w-40 sm:w-full" />
                    </div>
                    <Skeleton className="h-8 max-w-40 sm:w-full" />
                  </CardContent>
                </Card>
              ))
            : statCards.map((card) => (
                <Card key={card.id} className="p-4 space-y-1 sm:space-y-2">
                  <CardHeader className="p-0">
                    <CardTitle className="text-sm font-medium">
                      <div className="flex items-center gap-1 sm:gap-2">
                        <div className="border rounded-lg bg-secondary size-8 flex items-center justify-center shrink-0">
                          <card.icon className="size-4" />
                        </div>
                        <span className="text-sm truncate">{card.title}</span>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="text-lg sm:text-xl md:text-2xl font-bold">
                      {card.value}
                    </div>
                  </CardContent>
                </Card>
              ))}
        </div>

        <div className="flex gap-2 items-center justify-between flex-wrap">
          <SearchInput
            onSearch={(text) => onFilterTextBoxChanged(text)}
            placeholder="Search by email..."
            className="max-w-none sm:w-full md:max-w-[300px]"
          />
          <div className="flex gap-2 items-center justify-end w-full md:w-auto">
            <ConsolidatedFilterDropdown
              labels={labelOptionsForFilter}
              selectedLabels={selectedLabels}
              onLabelsChange={handleLabelsChange}
              seedAssignedOptions={seedAssignedOptions || []}
              selectedSeedAssigned={selectedSeedAssigned}
              onSeedAssignedChange={handleSeedAssignedChange}
              sendingVolumeOptions={sendingVolumeOptions || []}
              selectedSendingVolume={selectedSendingVolume}
              onSendingVolumeChange={handleSendingVolumeChange}
              subscriptionOptions={subscriptionOptions || []}
              selectedSubscription={selectedSubscription}
              onSubscriptionChange={handleSubscriptionChange}
            />
            {selectedRows.length > 0 && hasPermission("mailboxes:delete") && (
              <Button
                variant="destructive"
                onClick={() => setShowBulkDeleteDialog(true)}
                className="gap-2 w-auto group"
                size="sm"
              >
                <Trash className="h-4 w-4" />
                <span className="hidden sm:inline">
                  Delete Selected ({selectedRows.length})
                </span>
                <span className="sm:hidden">
                  Delete ({selectedRows.length})
                </span>
              </Button>
            )}
            {selectedRows.length > 0 && hasPermission("mailboxes:edit") && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-auto" size="sm">
                    <EllipsisVertical className="h-4 w-4" />
                    <span className="hidden sm:inline ml-2">Bulk Actions</span>
                    <span className="sm:hidden ml-1">Actions</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onSelect={() => {
                      setBulkLabelsAction("add");
                      setBulkLabelsSelected([]);
                      setShowBulkLabelsDialog(true);
                    }}
                  >
                    <TagIcon className="h-4 w-4" />
                    Add labels
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => {
                      setBulkLabelsAction("remove");
                      setBulkLabelsSelected([]);
                      setShowBulkLabelsDialog(true);
                    }}
                  >
                    <TagIcon className="h-4 w-4" />
                    Remove labels
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => handleBulkWarmupSenderProfiles(true)}
                  >
                    <Power className="h-4 w-4" />
                    Enable Warmup
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => handleBulkWarmupSenderProfiles(false)}
                  >
                    <PowerOff className="h-4 w-4" />
                    Disable Warmup
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <ColumnVisibility
              gridRef={gridRef}
              storageKey="sender-profiles-grid-columns"
              triggerClassName="[&>span]:hidden sm:[&>span]:block"
            />
          </div>
        </div>

        <DataTable
          ref={gridRef}
          rowData={filteredAccounts ?? []}
          columnDefs={columnDefs}
          height={550}
          showPagination={true}
          defaultColDef={{
            editable: false,
            resizable: true,
            sortable: true,
            filter: true,
          }}
          paginationProps={{
            pageSize: limit,
            totalPages: Math.ceil((warmupAccounts?.length || 0) / limit),
            currentPage: currentPage,
            onPageChange: (page: number) => {
              setCurrentPage(page);
            },
            totalEntries: warmupAccounts.length,
            rowsPerPage: limit,
            onRowsPerPageChange: (limit: number) => {
              setLimit(limit);
            },
          }}
          loading={isWarmupLoading}
          onSelectionChanged={onSelectionChanged}
          gridOptions={{
            animateRows: false,
            suppressColumnVirtualisation: true,
            suppressRowVirtualisation: true,
            rowBuffer: 1000,
            getRowId: (params) => params.data?._id,
            suppressPropertyNamesCheck: true,
            suppressScrollOnNewData: false,
            enableCellTextSelection: true,
            alwaysShowVerticalScroll: true,
          }}
        />
      </div>

      {/* Sender Profile Tutorial Modal */}
      <InitialPageVisitGuideModal type="sender" />

      {showBulkDeleteDialog && (
        <ConfirmActionPopup
          actionLabel="Delete Selected Senders"
          open={showBulkDeleteDialog}
          onOpenChange={setShowBulkDeleteDialog}
          title="Delete Selected Senders"
          description={`Are you sure you want to delete ${selectedRows.length} selected
      senders? This action cannot be undone.`}
          onConfirm={handleBulkDeleteSenders}
          withTextConfirmation={true}
          textToConfirm="confirm"
        />
      )}

      {showSingleDeleteDialog && (
        <ConfirmActionPopup
          actionLabel="Delete Sender"
          open={showSingleDeleteDialog}
          onOpenChange={setShowSingleDeleteDialog}
          title="Delete Sender"
          description={
            <>
              Are you sure you want to delete{" "}
              <span className="font-bold">{toDeleteSender?.email}</span> sender?
              This action cannot be undone.
            </>
          }
          withTextConfirmation={true}
          textToConfirm="confirm"
          onConfirm={confirmSingleDelete}
        />
      )}

      {showBulkLabelsDialog && (
        <Dialog
          open={showBulkLabelsDialog}
          onOpenChange={(open) => {
            setShowBulkLabelsDialog(open);
            if (!open) setBulkLabelsSelected([]);
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {bulkLabelsAction === "add"
                  ? "Add labels to selected senders"
                  : "Remove labels from selected senders"}
              </DialogTitle>
              <DialogDescription>
                {selectedRows.length} sender(s) selected.{" "}
                {bulkLabelsAction === "add"
                  ? "Select labels to add."
                  : "Select labels to remove."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-60 overflow-y-auto">
              {availableSenderLabels.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No Sender Profile labels. Create them in Settings → Labels (Sender
                  Profile tab).
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  {availableSenderLabels.map((label) => (
                    <label
                      key={label._id}
                      className="flex items-center gap-2 cursor-pointer rounded-md p-2 hover:bg-accent"
                    >
                      <input
                        type="checkbox"
                        checked={bulkLabelsSelected.includes(label._id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setBulkLabelsSelected((prev) => [...prev, label._id]);
                          } else {
                            setBulkLabelsSelected((prev) =>
                              prev.filter((id) => id !== label._id)
                            );
                          }
                        }}
                        className="rounded border-input"
                      />
                      <div
                        className="w-3 h-3 rounded-full shrink-0"
                        style={{ backgroundColor: label.color }}
                      />
                      <span className="text-sm">{label.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowBulkLabelsDialog(false);
                  setBulkLabelsSelected([]);
                }}
              >
                Cancel
              </Button>
              <Button
                disabled={
                  bulkLabelsSelected.length === 0 ||
                  isBulkAddingLabels ||
                  isBulkRemovingLabels
                }
                onClick={async () => {
                  if (bulkLabelsSelected.length === 0) return;
                  const senderIds = selectedRows.map((r) => r._id);
                  try {
                    if (bulkLabelsAction === "add") {
                      await bulkAddSenderLabels({ senderIds, labelIds: bulkLabelsSelected });
                    } else {
                      await bulkRemoveSenderLabels({
                        senderIds,
                        labelIds: bulkLabelsSelected,
                      });
                    }
                    setShowBulkLabelsDialog(false);
                    setBulkLabelsSelected([]);
                    setSelectedRows([]);
                  } catch {
                    // toasts handled in hooks
                  }
                }}
              >
                {isBulkAddingLabels || isBulkRemovingLabels
                  ? "Applying..."
                  : bulkLabelsAction === "add"
                    ? "Add labels"
                    : "Remove labels"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {showEditDialog && (
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Seed Account</DialogTitle>
              <DialogDescription>
                Update the first name, last name, and ESP type for {toEditSender?.email}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-firstName">First Name</Label>
                <Input
                  id="edit-firstName"
                  value={editFirstName}
                  onChange={(e) => setEditFirstName(e.target.value)}
                  placeholder="Enter first name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-lastName">Last Name</Label>
                <Input
                  id="edit-lastName"
                  value={editLastName}
                  onChange={(e) => setEditLastName(e.target.value)}
                  placeholder="Enter last name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-espType">ESP Type</Label>
                <Select value={editEspType} onValueChange={setEditEspType}>
                  <SelectTrigger id="edit-espType">
                    <SelectValue placeholder="Select ESP Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(espTypeDescription).map(([key, value]) => (
                      <SelectItem key={key} value={key}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowEditDialog(false);
                  setToEditSender(null);
                  setEditFirstName("");
                  setEditLastName("");
                  setEditEspType("");
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateSender}
                disabled={isUpdatingSender || !editFirstName || !editLastName}
              >
                {isUpdatingSender ? "Updating..." : "Update"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default SenderPageContent;
